import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useIsFetching } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { generateGUID } from '../util/guid.js';
export const PermissionBoundaryContext = createContext({
    errorKeys: [],
    addPermissionError: () => { },
    clearPermissionErrors: () => { },
    getCapturedKeys: () => [],
    captureKeys: () => {
        return '';
    },
    releaseKeys: () => { },
    hasCollectedAllErrors: true,
});
export const PermissionBoundaryContextProvider = (props) => {
    const { children } = props;
    const [errorKeys, setErrorKeys] = useState([]);
    const [capturedKeys, setCapturedKeys] = useState([]);
    const isFetching = useIsFetching();
    // Track if we've seen any errors yet - once we have, wait for queries to settle
    const [hasSeenErrors, setHasSeenErrors] = useState(false);
    const [hasCollectedAllErrors, setHasCollectedAllErrors] = useState(true);
    const addPermissionError = useCallback((key) => {
        setErrorKeys((x) => [...x, key]);
        setHasSeenErrors(true);
        setHasCollectedAllErrors(false);
    }, [setErrorKeys]);
    const clearPermissionErrors = useCallback(() => {
        setErrorKeys([]);
        setHasSeenErrors(false);
        setHasCollectedAllErrors(true);
    }, [setErrorKeys]);
    // Once we've seen errors, wait for all fetching to complete before marking as settled
    useEffect(() => {
        if (hasSeenErrors && isFetching === 0) {
            setHasCollectedAllErrors(true);
        }
    }, [hasSeenErrors, isFetching]);
    const getCapturedKeys = useCallback(() => capturedKeys, [capturedKeys]);
    const captureKeys = useCallback((key) => {
        const guid = generateGUID();
        setCapturedKeys((existingCapturedKeys) => [
            ...existingCapturedKeys,
            {
                id: guid,
                key,
            },
        ]);
        return guid;
    }, []);
    const releaseKeys = useCallback((id) => {
        setCapturedKeys((existingCapturedKeys) => existingCapturedKeys.filter((x) => x.id !== id));
    }, []);
    const value = useMemo(() => ({
        errorKeys,
        addPermissionError,
        clearPermissionErrors,
        getCapturedKeys,
        captureKeys,
        releaseKeys,
        hasCollectedAllErrors,
    }), [
        errorKeys,
        addPermissionError,
        clearPermissionErrors,
        getCapturedKeys,
        captureKeys,
        releaseKeys,
        hasCollectedAllErrors,
    ]);
    return _jsx(PermissionBoundaryContext.Provider, { value: value, children: children });
};
// Hook to use the permission boundary context
export const usePermissionBoundaryData = () => {
    return useContext(PermissionBoundaryContext);
};
/**
 * Resets the PermissionBoundaryContext when the cacheKey changes
 * @param props - ResetCacheKey: when this changes, permission errors are cleared. Typically bound to the route, to reset on page change.
 * @returns nothing, pure side-effect component
 */
export const PermissionBoundaryReset = (props) => {
    const { resetCacheKey } = props;
    const { clearPermissionErrors } = usePermissionBoundaryData();
    useEffect(() => {
        clearPermissionErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetCacheKey]);
    return null;
};
//# sourceMappingURL=data:application/json;base64,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